/**
 * What the assistant is told about /creatives (spec 2026-10-21 d3-d4 chat §4): a LookerChatContext with template.id
 * "creatives", read by the bubble in mode 'creatives' with CREATIVE_ADVISOR, whose page rules explain its keys. Built
 * from the CSV's own input, so the chat and the file read one view. A summary, bounded whatever the number of
 * creatives: the table's first LOOKER_CHAT_ROW_CAP rows in the page's order, and the rows of that table that carry
 * Top or Fatigue, up to LOOKER_CHAT_ROW_CAP each, with the same columns; each tile's rows plus its rowsOmitted tell
 * how many it holds (C13). Cells are worded as the page draws them, and a figure the page prints as "—" is null. No
 * total: the page has none. A failed read is told as failed; the page tells nothing while the table loads. Pure.
 *
 * @module services/creatives/creativeChatContext
 */
import { CHANNEL_LABEL, campaignStateText, type TFn } from '@/services/channels/channelFormat';
import {
  freshnessOfState, LOOKER_CHAT_ROW_CAP, type LookerChatContext, type LookerChatTile,
} from '@/services/looker/lookerChatContext';
import type { DateRange } from '@/services/looker/lookerDateRange';
import { unit } from '@/services/overview/boxTableParts';
import { DASH, fmtCount, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import type { CreativeCsvInput } from './creativeCsv';
import { creativeFormatLabel } from './creativeFormat';
import {
  ageText, channelFilterText, costPerResultText, creativeNameOf, emptyTextOf, engagementText, footnotesOf,
  formatFilterLabel, labelTextsOf, objectiveFilterText, resultCellOf, sortLabel,
} from './creativeText';
import type { CreativeRow } from './creativeTypes';

export const CREATIVES_TEMPLATE_ID = 'creatives';

/** The CSV's input and the window before the period, so the chat and the file read one view (spec §4.2). */
export interface CreativeChatInput extends CreativeCsvInput {
  previous: DateRange;
}

/** A figure as the page draws it; the page's "—" is null (spec §3.2's table). */
const shown = (text: string): string | null => (text === DASH ? null : text);

/** The CSV's columns without "Ad ID", in its order, with its units in the headers (spec §4.2). */
const columnsOf = (t: TFn, currency: string | null): string[] => [
  t('creatives.col.creative'), t('creatives.csv.channel'), t('creatives.format.label'), t('creatives.csv.campaign'),
  t('creatives.csv.status'), t('creatives.csv.labels'), t('creatives.col.age'), unit(t('creatives.col.spend'), currency),
  t('creatives.col.impressions'), t('creatives.col.clicks'), unit(t('creatives.col.ctr'), '%'),
  unit(t('creatives.col.cpc'), currency), unit(t('creatives.col.cpm'), currency), t('creatives.col.result'),
  t('creatives.csv.resultMetric'), unit(t('creatives.col.costPerResult'), currency), t('creatives.col.engagement'),
  unit(t('creatives.col.viewRate'), '%'),
];

/**
 * One creative as CreativeTable draws it: a word keeps the page's word, or the file's in สถานะ, where the page draws no
 * chip for an unknown state ("—", as in the CSV), and is null where the page draws nothing (no campaign, no label, no
 * Result); a figure is null where the page prints "—".
 */
const rowOf = (r: CreativeRow, columns: readonly string[], currency: string | null, t: TFn): Record<string, string | null> => {
  const labels = labelTextsOf(r, t);
  const result = resultCellOf(r, t);
  const cells = [
    creativeNameOf(r, t), CHANNEL_LABEL[r.platform], creativeFormatLabel(r.format, t),
    r.campaignName.trim() === '' ? null : r.campaignName, campaignStateText(r.state, t),
    labels.length > 0 ? labels.join('; ') : null, shown(ageText(r.age, t)),
    shown(fmtMoney(r.spend, currency)), shown(fmtCount(r.impressions)), shown(fmtCount(r.clicks)), shown(fmtPct(r.ctr)),
    shown(fmtMoney(r.cpc, currency)), shown(fmtMoney(r.cpm, currency)),
    shown(result.value), result.metric, shown(costPerResultText(r, currency, t)), engagementText(r.engagement, t),
    shown(fmtPct(r.viewRate)),
  ];
  return Object.fromEntries(columns.map((c, i) => [c, cells[i]]));
};

/** One tile of creatives in the table's order: the first LOOKER_CHAT_ROW_CAP, and how many more it holds. */
const creativeTile = (
  title: string, rows: readonly CreativeRow[], columns: string[], currency: string | null, through: string | null, t: TFn,
): LookerChatTile => {
  const sent = rows.slice(0, LOOKER_CHAT_ROW_CAP);
  return {
    title,
    type: 'table',
    status: sent.length === 0 ? 'empty' : 'ok',
    columns,
    rows: sent.map((r) => rowOf(r, columns, currency, t)),
    rowsOmitted: rows.length - sent.length,
    moreRowsThanLoaded: false,
    ...(sent.length > 0 && through !== null ? { resultThrough: through } : {}),
  };
};

const tilesOf = (input: CreativeChatInput, currency: string | null, t: TFn): LookerChatTile[] => {
  const { view, windows } = input;
  const title = t('creatives.box.table');
  if (view.table.status === 'failed') {
    return [{ title, type: 'table', status: 'failed', columns: [], rows: [], rowsOmitted: 0, moreRowsThanLoaded: false }];
  }
  // Loading (the facts or the Result definition): not told yet (the page mounts no chat until the table is read).
  if (view.table.status !== 'ready') return [];
  const { rows, inCurrency } = view.table.data;
  // The notes the page prints under the table, from the inputs the page passes them (CreativePerformance.tsx).
  const notes = footnotesOf({
    resultEnd: windows.resultEnd, mappingFailed: input.mapping?.failed === true, unknownCurrency: view.unknownCurrency,
    reach: rows.some((r) => r.metric === 'reach'),
  }, t);
  if (rows.length === 0) {
    const scope = input.allWorkspaces ? t('looker.allWorkspaces') : input.workspaceName ?? '';
    const line = emptyTextOf({ inCurrency }, scope, input.filters, t);
    return [{ title, type: 'table', status: 'empty', columns: [], rows: [], rowsOmitted: 0, moreRowsThanLoaded: false, coverageNotes: [line, ...notes] }];
  }
  const columns = columnsOf(t, currency);
  // Only a period the cut shortened leaves days out, as RESULT CUT says resultThrough means (D1's and D2's cutApplied).
  const through = windows.resultEnd !== null && windows.resultEnd < windows.end ? windows.resultEnd : null;
  const tile = (name: string, of: readonly CreativeRow[]): LookerChatTile => creativeTile(name, of, columns, currency, through, t);
  return [
    { ...tile(title, rows), coverageNotes: notes },
    // The labels are weighed before any filter (creativeView); these tiles hold the table's rows that carry them (C14: all).
    tile(t('creatives.chat.topTile'), rows.filter((r) => r.top)),
    tile(t('creatives.chat.fatigueTile'), rows.filter((r) => r.fatigueDrop !== null)),
  ];
};

export const buildCreativeChatContext = (input: CreativeChatInput, t: TFn): LookerChatContext => {
  const { view, filters: f, windows: w } = input;
  const currency = view.money?.currency ?? null;
  return {
    ...freshnessOfState(input.freshness),
    kind: 'looker',
    template: { id: CREATIVES_TEMPLATE_ID, title: t('creatives.title') },
    scope: input.allWorkspaces ? { allWorkspaces: true, workspaceName: null } : { allWorkspaces: false, workspaceName: input.workspaceName },
    // As the file's filters and sort lines word them; the period travels as `period`.
    filters: [
      { title: t('overview.filters.objective'), value: objectiveFilterText(f.objective, t) },
      { title: t('overview.filters.channels'), value: channelFilterText(f.channels, t) },
      { title: t('creatives.format.label'), value: formatFilterLabel(f.format, t) },
      { title: t('creatives.sort.label'), value: sortLabel(f.sort, t) },
    ],
    period: { start: w.start, end: w.end, previousStart: input.previous.start, previousEnd: input.previous.end },
    latestDataDate: input.dataDate,
    currency,
    // As D2's: with no currency too, when the page left an unknown currency out.
    ...(view.money?.unknownExcluded ? { currencyUnknownExcluded: true as const } : {}),
    tiles: tilesOf(input, currency, t),
  };
};
